import React, { useEffect, useMemo } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Fade,
  Flex,
  Heading,
  HStack,
  Spacer,
} from '@chakra-ui/react'
import ContainerContent from '../../components/ui/Layout/ContainerContent'

import { useNavigate, useParams } from 'react-router-dom'
import SetupDataSource from './components/SetupDataSource'
import SetupReportStructure from './components/SetupReportStructure'
import SetupFilters from './components/SetupFilters'
import SetupSummary from './components/SetupSummary'
import Stepper from '../../components/features/Wizard/Stepper/Stepper'
import rootStore from '../../store/RootStore'
import { observer } from 'mobx-react'
import SetupMetadata from './components/SetupMetadata'
import ReactGA from 'react-ga4'
import LinkedLogo from '../../components/ui/LinkedLogo'
import { putReportSourceById, useWizardUpload } from '@services/api/wizardAPI'
import ReactJson from 'react-json-view'
import Message from '../../components/features/Wizard/Message/Message'
import { useUpdateReportSourceMutation } from './hooks/useUpdateReportSourceMutation'
import { useQueryClient } from '@tanstack/react-query'
import { useFetchStructureQuery } from './hooks/useFetchStructureQuery'

const LAST_STEP = 5

const WizardPage = observer(() => {
  const {
    wizardStore: {
      setCurrentStep,
      savingStructure,
      generateReport,
      error,
      canGenerate,
      setSourceId,
      sourceId,
      selectedBlueprintId,
      setError,
      setCanGenerate,
      metadata
    },
    planStore: { currentPlan },
    dropzoneStore: { file, setUploadError: setDropzoneError },
  } = rootStore

  const queryClient = useQueryClient()
  const params = useParams()
  const step = parseInt(params?.step ?? '1')

  const {
    handleUpload: startUploadFile,
    uploadError,
    setUploadError,
    uploading,
  } = useWizardUpload()
  const { data: reportBlueprint } = useFetchStructureQuery(sourceId)
  const updateReportSourceMutation = useUpdateReportSourceMutation()
  const navigate = useNavigate()

  const stepTitle = useMemo(() => {
    return [
      'Step 1: Data Source and Blueprint',
      'Step 2: Hard Coded Title',
      'Step 3: Overview of filters per section ',
      'Step 4: Summary - Hard Coded Title',
      'Step 5: Name & Notes - Hard Coded Title',
    ][step - 1]
  }, [step])

  useEffect(() => {
    setCurrentStep(step - 1)
    setError(null)
  }, [setCurrentStep, setError, step])

  useEffect(() => {
    if (uploadError) {
      setError(uploadError)
      setCanGenerate(!uploadError)
    }
  }, [error, setError, setCanGenerate, uploadError])

  /**
   * Google Analytics
   */
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: `/create-report/${step}`,
      title: stepTitle[step - 1],
    })
  }, [step, stepTitle])

  const handleNextStepClick = async () => {
    updateReportSourceMutation.mutate({
      sourceId,
      reportSource: {
        ...reportBlueprint,
        components: reportBlueprint.children.map(item => {
          return {
            ...item,
            ComponentType: 1,
          }
        }),
      },
    })

    navigate(
      `/create-report/${step + 1}${sourceId ? `?sourceId=${sourceId}` : ''}`,
    )
  }

  const handleLastStepClick = async () => {
    reportBlueprint.name = metadata.reportName
    reportBlueprint.shortDescription = metadata.shortDescription
    reportBlueprint.internalNotes = metadata.internalNotes
    await putReportSourceById(sourceId, reportBlueprint)
    await generateReport()
    if (step === LAST_STEP) return navigate('/', { replace: true })
  }

  const handleUploadAndNextStepClick = async () => {
    const uploadedSourceId = await startUploadFile({
      file,
      blueprintId: selectedBlueprintId,
      planId: currentPlan.planId,
    })

    if (uploadedSourceId) {
      setSourceId(uploadedSourceId)
      navigate(
        `/create-report/${step + 1}${
          uploadedSourceId ? `?sourceId=${uploadedSourceId}` : ''
        }`,
      )
    }
  }

  useEffect(() => {
    if (!file) {
      setUploadError(null)
    }
  }, [file, setUploadError])

  const handleErrorMessageOnClose = () => {
    setDropzoneError(null)
    setError(null)
    setUploadError(null)
  }

  useEffect(() => {
    if (!currentPlan.planId) {
      navigate('/')
    }
    return () => {
      setUploadError(null)
    }
  }, [currentPlan.planId, navigate, setUploadError])

  return (
    <>
      <Flex bg="brand.blue">
        <HStack w={'100%'}>
          <LinkedLogo />
          <ContainerContentStyled>
            <Heading
              as="h1"
              ml={'20px'}
              color="white"
              fontSize={['xl', 'xl', 'xl', 'xl', '3xl']}>
              Create Report - {currentPlan?.planName}
            </Heading>
          </ContainerContentStyled>
        </HStack>
      </Flex>
      <ContainerContentStyled>
        <Stepper />
        {updateReportSourceMutation.isLoading && (
          <Message
            message="Saving report source..."
            type="info"
          />
        )}
        <Card
          mb={8}
          backgroundColor="white">
          <CardHeader textStyle="h2">{stepTitle}</CardHeader>
          <CardBody overflowX="auto">
            <Fade
              in={!!uploadError}
              unmountOnExit>
              <Message
                message={uploadError}
                type="error"
                onClose={handleErrorMessageOnClose}
              />
            </Fade>

            {step === 1 && <SetupDataSource />}
            {step === 2 && <SetupReportStructure />}
            {step === 3 && <SetupFilters />}
            {step === 4 && <SetupSummary />}
            {step === 5 && <SetupMetadata />}
          </CardBody>
          <CardFooter>
            <HStack
              flex={1}
              gap={2}>
              <Spacer />
              {step > 1 && (
                <Button
                  variant="outline"
                  colorScheme="gray"
                  onClick={() =>
                    !savingStructure &&
                    navigate(
                      `/create-report/${step - 1}${
                        sourceId ? `?sourceId=${sourceId}` : ''
                      }`,
                    )
                  }>
                  Previous
                </Button>
              )}

              {step === 1 && (
                <NextStepButton
                  onClick={handleUploadAndNextStepClick}
                  isLoading={savingStructure || uploading}
                  disabled={!(file || savingStructure)}>
                  {'Next'}
                </NextStepButton>
              )}

              {step > 1 && step < 5 && (
                <NextStepButton
                  onClick={handleNextStepClick}
                  isLoading={savingStructure}
                  disabled={!!error}>
                  {step === LAST_STEP ? 'Generate' : 'Next'}
                </NextStepButton>
              )}

              {step === LAST_STEP && (
                <NextStepButton
                    onClick={handleLastStepClick}
                    isLoading={savingStructure}
                    disabled={!canGenerate}
                  >
                    Generate
                  </NextStepButton>
              )}
            </HStack>
          </CardFooter>
        </Card>
        <ReactJson src={reportBlueprint} />
      </ContainerContentStyled>
    </>
  )
})

const NextStepButton = ({ onClick, isLoading, disabled, children }) => (
  <Button
    onClick={onClick}
    isLoading={isLoading}
    spinnerPlacement="end"
    variant={disabled ? 'outline' : 'solid'}
    colorScheme={disabled ? 'gray' : 'orange'}
    isDisabled={disabled}>
    {children}
  </Button>
)

const ContainerContentStyled = props => (
  <ContainerContent
    maxW={1170}
    p={'32px 0'}
    {...props}
  />
)
export default WizardPage
